import Image, { type ImageProps } from 'next/image';

export { IMAGE_SIZES } from './sizes';

type CdnImageProps = Omit<ImageProps, 'quality'> & {
  quality?: number;
};

/**
 * CdnImage - Next.js Image component optimized for Cloudflare CDN
 *
 * Wraps Next.js Image with sensible defaults for CDN-hosted images.
 * All NerdWallet images (cdn-cgi, Tachyon, etc.) are optimized through Cloudflare.
 * External images and SVGs are automatically marked as unoptimized.
 *
 * Supports both string URLs and StaticImageData objects (from image imports).
 */
export const CdnImage = ({ quality = 80, priority, ...props }: CdnImageProps) => {
  let src = '';

  if (typeof props.src === 'string') {
    src = props.src;
  } else if (typeof props.src === 'object' && props.src !== null) {
    // StaticImageData or StaticRequire from image imports
    if ('src' in props.src) {
      src = props.src.src;
    } else if ('default' in props.src) {
      src = props.src.default.src;
    }
  }

  if (!src) {
    return null;
  }

  // Auto-detect images that should skip optimization
  const isSvg = src.toLowerCase().endsWith('.svg');
  const isNerdWalletImage = src.includes('nerdwallet.com') || src.includes('nerdwallet.biz');
  const shouldSkipOptimization = isSvg || !isNerdWalletImage;

  return (
    <Image
      {...props}
      alt={props.alt}
      quality={quality}
      priority={priority}
      unoptimized={shouldSkipOptimization ? true : props.unoptimized}
    />
  );
};
